import { alpha, Stack, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import {
  DEFAULT_LEFT_DRAWER_CLOSED_WIDTH,
  useKeyboard,
  EditCommandsEnum,
  scrollToElement,
  Events,
  useAssistantLoadingStore,
  usePageLayoutStore,
  FlowResourceType,
  useEditorStore,
} from '@diana/core';
import { useState } from 'react';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { useAiAcceptRejectCommands } from '../../Support/useAiAcceptRejectCommands';

interface IAcceptRejectButtonProps {
  text: string;
  keys?: string[];
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const ActionButton = (props: IAcceptRejectButtonProps) => {
  const { text, keys, onClick, isLoading = false, disabled = false } = props;
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';

  const loadingBackground = isDarkMode
    ? palette.action.disabledBackground
    : `${alpha(palette.common.white, 0.12)} !important`;
  const staleBackground = isDarkMode ? palette.secondary.main : palette.secondary.textForeground;
  const hoverBackground = isDarkMode ? palette.action.focus : alpha(palette.common.white, 0.08);

  return (
    <LoadingButton
      disabled={disabled}
      loading={isLoading}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      variant="contained"
      color="secondary"
      sx={{
        height: 28,
        backgroundColor: isLoading ? loadingBackground : staleBackground,
        borderColor: isDarkMode ? palette.secondary.border : palette.grey['A700'],
        borderRadius: 1.5,
        p: 0.5,
        '&:hover': { backgroundColor: hoverBackground },
        '& .MuiLoadingButton-loadingIndicator': { color: alpha(palette.common.white, 0.32) },
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.25} sx={{ opacity: isLoading ? 0 : 1 }}>
        <Typography
          color={isDarkMode ? palette.text.primary : palette.common.white}
          sx={{ px: 0.5, textWrap: 'nowrap', textTransform: 'none' }}
        >
          {text}
        </Typography>
        {keys ? (
          <Stack direction="row" alignItems="center">
            {keys.map((key, index) => {
              return (
                <Typography
                  key={`hotkey-${text}-${index}`}
                  alignItems="center"
                  sx={{
                    width: 16,
                    color: isDarkMode ? palette.text.disabled : alpha(palette.common.white, 0.32),
                  }}
                >
                  {key}
                </Typography>
              );
            })}
          </Stack>
        ) : null}
      </Stack>
    </LoadingButton>
  );
};

export const AcceptRejectBar = () => {
  const { palette } = useTheme();
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const isLeftDrawerOpen = usePageLayoutStore((store) => store.isLeftDrawerOpen);
  const leftDrawerWidth = usePageLayoutStore((store) => store.leftDrawerWidth);
  const rightDrawerWidth = usePageLayoutStore((store) => store.rightDrawerWidth);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const isModalOpen = useFlowBuilder2PageStore((s) => !!s.activeModal);
  const draftInView = useFlowBuilder2PageStore((s) => s.draftInView);
  const setDraftInView = useFlowBuilder2PageStore((s) => s.setDraftInView);
  const setIsScrolling = useFlowBuilder2PageStore((s) => s.setIsScrolling);
  const firstPendingLayoutId = useFlowBuilder2PageStore((s) => s.aiPendingStatuses.layouts[0]);
  const pendingFlows = useFlowBuilder2PageStore((s) => s.aiPendingStatuses.flows);

  const flowResourceType = useEditorStore((s) => s.flow?.properties.flowResourceType);

  const hasDraftInView = draftInView.length > 0;
  const isEntityResourceType = flowResourceType === FlowResourceType.Entity;
  const isAwayFromDraft = pendingFlows.length === 0 && !hasDraftInView && !isEntityResourceType;

  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const [isRejecting, setIsRejecting] = useState<boolean>(false);

  const resetLoading = () => {
    setIsAccepting(false);
    setIsRejecting(false);
  };

  const { updatePendingStatus } = useAiAcceptRejectCommands({ onFinally: resetLoading });

  const handleAccept = () => {
    setIsAccepting(true);
    updatePendingStatus({ command: EditCommandsEnum.Accept });
  };

  const handleDiscard = () => {
    setIsRejecting(true);
    updatePendingStatus({ command: EditCommandsEnum.Decline, updateCache: true });
  };

  const handleReturnToDraft = () => {
    Events.SetFlowBuilderActiveTask.post({ layoutId: firstPendingLayoutId });
    scrollToElement({ id: firstPendingLayoutId, isBoundingClientRect: true, padding: 60 });
    setIsScrolling(true);
    setDraftInView([firstPendingLayoutId]);
    setTimeout(() => setIsScrolling(false), 1500);
  };

  const currentLeftDrawerWidth = isLeftDrawerOpen
    ? leftDrawerWidth
    : DEFAULT_LEFT_DRAWER_CLOSED_WIDTH;
  const positionOffset = (rightDrawerWidth - currentLeftDrawerWidth) / 2;

  const actions: IAcceptRejectButtonProps[] = isAwayFromDraft
    ? [{ text: t('labels.returnToUpdates'), onClick: handleReturnToDraft }]
    : [
        {
          text: t('labels.discard'),
          keys: ['⌘', '⌫'],
          onClick: handleDiscard,
          isLoading: isRejecting,
          disabled: isChatLoading,
        },
        {
          text: t('labels.accept'),
          keys: ['⌘', '⏎'],
          onClick: handleAccept,
          isLoading: isAccepting,
          disabled: isChatLoading,
        },
      ];

  const title = isAwayFromDraft ? t('labels.pendingChanges') : t('labels.reviewChanges');

  useKeyboard({
    keys: 'Backspace',
    handler: handleDiscard,
    withCmdOrCtrl: true,
    enabled: !isModalOpen,
  });

  useKeyboard({
    keys: 'Enter',
    handler: handleAccept,
    withCmdOrCtrl: true,
    enabled: !isModalOpen,
  });

  const isDarkMode = palette.mode === 'dark';

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        position: 'fixed',
        zIndex: 100,
        bottom: 16,
        right: `calc(50% + ${positionOffset}px)`,
        transform: 'translate(50%)',
        p: 0.5,
        borderRadius: 2,
        backgroundColor: isDarkMode ? palette.secondary.main : palette.secondary.textForeground,
        transition: 'right 0.2s',
      }}
    >
      <Typography
        color={isDarkMode ? palette.text.secondary : palette.text.placeholder}
        sx={{ p: 0.5, textWrap: 'nowrap' }}
      >
        {title}
      </Typography>
      <Stack direction="row" alignItems="center" gap={0.5}>
        {actions.map((actionProps, index) => {
          return <ActionButton key={`accept-reject-action-${index}`} {...actionProps} />;
        })}
      </Stack>
    </Stack>
  );
};
